import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronLeft, SlidersHorizontal,
  CalendarDays, LayoutList, Rocket, ClipboardList,
  FileEdit, CircleCheckBig, FileText, RefreshCw, Wrench,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { ThemeToggle } from '../../_shared/components';
import { CampaignDetailSidebar } from './CampaignDetailSidebar';
import { MonthPickerPopover } from './MonthPickerPopover';
import type { Campaign, LauncherStatus } from './types';
import { useLauncherCampaigns } from '../hooks/useLauncherCampaigns';
import { useLauncherCampaign } from '../hooks/useLauncherCampaign';
import { useCampaignTypeConfigs, useSyncStatus, useTriggerCampaignSync } from '../hooks/useCampaignTypeConfig';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { patchCampaignDetails } from '../event-manager-client';
import { subtypeColor } from './subtype-colors';

// ── Helpers ──────────────────────────────────────────────────────────────────

const STORAGE_KEY_VIEW = 'launcher_list_view';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function todayIso(): string {
  // Local (not UTC) — avoids the "today" highlight landing on the wrong day near midnight.
  return toIso(new Date());
}

function loadPersistedView(): 'calendar' | 'table' {
  try { return (localStorage.getItem(STORAGE_KEY_VIEW) as 'calendar' | 'table') ?? 'calendar'; } catch { return 'calendar'; }
}
function persistView(v: 'calendar' | 'table') {
  try { localStorage.setItem(STORAGE_KEY_VIEW, v); } catch {}
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Campaigns synced from Salesforce may have no StartDate — every caller of
// these helpers must tolerate that instead of crashing the whole list view.
const NO_DATE_KEY = '9999-99';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'No date';
  const [year, month, day] = iso.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const dow = d.toLocaleDateString('en-US', { weekday: 'short' });
  const rest = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dow}, ${rest}`;
}

function monthKey(iso: string | null | undefined): string {
  if (!iso) return NO_DATE_KEY;
  const [year, month] = iso.split('-').map(Number);
  return `${year}-${String(month).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  if (key === NO_DATE_KEY) return 'No Date';
  const [year, month] = key.split('-').map(Number);
  return `${MONTHS[month - 1]} ${year}`;
}

// ── Subtype Chip ─────────────────────────────────────────────────────────────

function SubtypeChip({ subtype }: { subtype: string | null }) {
  const c = subtypeColor(subtype);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${c.fg}`}>
      <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${c.dot}`} />
      {subtype ?? 'Unknown'}
    </span>
  );
}

// ── Status Icon ──────────────────────────────────────────────────────────────

function LauncherStatusIcon({ status }: { status: LauncherStatus }) {
  if (status === 'launched') {
    return (
      <span title="Launched" className="inline-flex items-center text-matcha-500">
        <CircleCheckBig className="w-4 h-4" />
      </span>
    );
  }
  return (
    <span title="Draft" className="inline-flex items-center text-nightshade-400 dark:text-nightshade-500">
      <FileEdit className="w-4 h-4" />
    </span>
  );
}

// ── Calendar Chip Popover ────────────────────────────────────────────────────

function CalendarChipPopover({
  campaign,
  onNavigate,
  anchorRef,
}: {
  campaign: Campaign;
  onNavigate: (id: string, target: 'manager' | 'launcher') => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6 + window.scrollY, left: rect.left + window.scrollX });
  }, [anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      style={{ position: 'absolute', top: pos.top, left: pos.left }}
      className="z-[9999] w-60 bg-nightshade-900 text-white rounded-xl p-3 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150"
      onClick={e => e.stopPropagation()}
    >
      <h6 className="font-semibold text-sm mb-1 leading-snug">{campaign.name}</h6>
      <div className="font-mono text-[11px] text-nightshade-400 mb-1">
        {formatDate(campaign.event_date)}
        {campaign.subtype && <> &middot; {campaign.subtype}</>}
      </div>
      <div className="font-mono text-[11px] text-nightshade-400 mb-2">
        <strong className="text-white">{campaign.member_count.toLocaleString()}</strong> members
      </div>
      <div className="flex gap-1.5 pt-2 border-t border-white/10">
        <button
          onClick={() => onNavigate(campaign.id, 'manager')}
          className="flex-1 flex items-center justify-center gap-1.5 h-7 rounded-md bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition-colors"
        >
          <ClipboardList className="w-3.5 h-3.5" /> Manager
        </button>
        <button
          onClick={() => onNavigate(campaign.id, 'launcher')}
          className="flex-1 flex items-center justify-center gap-1.5 h-7 rounded-md bg-tangerine-500 text-white text-xs font-medium hover:bg-tangerine-600 transition-colors"
        >
          <Rocket className="w-3.5 h-3.5" /> Dispatch
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Calendar Chip ────────────────────────────────────────────────────────────

function CalendarChip({
  campaign,
  isOpen,
  onToggle,
  onNavigate,
}: {
  campaign: Campaign;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: (id: string, target: 'manager' | 'launcher') => void;
}) {
  const c = subtypeColor(campaign.subtype);
  const isDraft = campaign.launcher_status === 'draft';
  const chipRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative" ref={chipRef}>
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`w-full text-left rounded-lg p-1.5 text-[11px] leading-tight cursor-pointer transition-all duration-100 hover:-translate-y-px hover:shadow-md border ${c.bg} ${c.fg} ${c.border} ${isDraft ? 'opacity-75' : ''}`}
      >
        <div className="font-semibold truncate text-[10.5px] leading-snug">
          {campaign.name}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          {isDraft
            ? <FileEdit className="w-3 h-3 opacity-50 flex-shrink-0" />
            : <CircleCheckBig className="w-3 h-3 opacity-70 flex-shrink-0" />
          }
          <span className="text-[10px] opacity-70 truncate">
            {campaign.subtype ?? campaign.type}
          </span>
          {campaign.type === 'Livestream' && (
            <span className="ml-auto text-[9px] font-mono opacity-60 flex-shrink-0">Remote</span>
          )}
        </div>
      </button>
      {isOpen && <CalendarChipPopover campaign={campaign} onNavigate={onNavigate} anchorRef={chipRef} />}
    </div>
  );
}

// ── Month Calendar ──────────────────────────────────────────────────────────

function MonthCalendar({
  campaigns,
  viewYear,
  viewMonth,
  onNavigate,
}: {
  campaigns: Campaign[];
  viewYear: number;
  viewMonth: number;
  onNavigate: (id: string, target: 'manager' | 'launcher') => void;
}) {
  const [popId, setPopId] = useState<string | null>(null);
  const today = todayIso();

  // Close popover on outside click
  useEffect(() => {
    const close = () => setPopId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Group campaigns by date
  const byDate = useMemo(() => {
    const map = new Map<string | null, Campaign[]>();
    for (const c of campaigns) {
      const existing = map.get(c.event_date) ?? [];
      existing.push(c);
      map.set(c.event_date, existing);
    }
    return map;
  }, [campaigns]);

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: Array<{ iso: string; day: number; inMonth: boolean }> = [];
  for (let i = 0; i < startDow; i++) {
    const d = new Date(viewYear, viewMonth, -startDow + i + 1);
    cells.push({ iso: toIso(d), day: d.getDate(), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ iso: toIso(new Date(viewYear, viewMonth, d)), day: d, inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const d = new Date(viewYear, viewMonth + 1, cells.length - startDow - daysInMonth + 1);
    cells.push({ iso: toIso(d), day: d.getDate(), inMonth: false });
  }

  const weeks: typeof cells[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    <div className="bg-white dark:bg-nightshade-900 rounded-2xl border border-nightshade-200 dark:border-nightshade-700 overflow-hidden" onClick={() => setPopId(null)}>
      {/* Header with month */}
      <div className="px-6 pt-5 pb-4">
        <h2 className="text-2xl font-bold tracking-tight text-nightshade-900 dark:text-nightshade-50">
          {MONTHS[viewMonth]}{' '}
          <span className="text-nightshade-400 font-normal text-xl ml-1">{viewYear}</span>
        </h2>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-t border-nightshade-200 dark:border-nightshade-700">
        {DOW.map((d, i) => (
          <div
            key={d}
            className={`text-center py-2 text-[11px] font-mono font-medium uppercase tracking-wider border-b border-nightshade-200 dark:border-nightshade-700 ${
              i >= 5 ? 'text-nightshade-300 dark:text-nightshade-600' : 'text-nightshade-500 dark:text-nightshade-400'
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map(({ iso, day, inMonth }, di) => {
            const isToday = iso === today;
            const dayCampaigns = byDate.get(iso) ?? [];
            const isWeekend = di >= 5;

            return (
              <div
                key={iso}
                className={`min-h-[120px] border-r border-b border-nightshade-100 dark:border-nightshade-800 last:border-r-0 p-1.5 relative ${
                  !inMonth ? 'opacity-40 bg-nightshade-50/50 dark:bg-nightshade-950/30' : isWeekend ? 'bg-nightshade-50/30 dark:bg-nightshade-950/20' : ''
                }`}
              >
                <span className={`text-sm font-medium inline-flex items-center justify-center w-6 h-6 ${
                  isToday
                    ? 'bg-nightshade-900 dark:bg-nightshade-100 text-white dark:text-nightshade-900 rounded-full'
                    : 'text-nightshade-900 dark:text-nightshade-200'
                }`}>
                  {day}
                </span>
                <div className="flex flex-col gap-1 mt-1">
                  {dayCampaigns.map(campaign => (
                    <CalendarChip
                      key={campaign.id}
                      campaign={campaign}
                      isOpen={popId === campaign.id}
                      onToggle={() => setPopId(prev => prev === campaign.id ? null : campaign.id)}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Table View ──────────────────────────────────────────────────────────────

function CampaignTable({
  campaigns,
  onNavigate,
  onOpenDetails,
  tableRef,
}: {
  campaigns: Campaign[];
  onNavigate: (id: string, target: 'manager' | 'launcher') => void;
  onOpenDetails: (id: string) => void;
  tableRef: React.RefObject<HTMLDivElement | null>;
}) {
  // Group campaigns by month
  const grouped = useMemo(() => {
    const map = new Map<string, Campaign[]>();
    for (const c of campaigns) {
      const mk = monthKey(c.event_date);
      const arr = map.get(mk) ?? [];
      arr.push(c);
      map.set(mk, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [campaigns]);

  return (
    <div ref={tableRef} className="bg-white dark:bg-nightshade-900 rounded-2xl border border-nightshade-200 dark:border-nightshade-700 overflow-auto max-h-[calc(100vh-280px)]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-nightshade-100 dark:bg-nightshade-800 text-[11px] font-mono uppercase tracking-wider text-nightshade-500 dark:text-nightshade-400 font-medium">
            <th className="text-left px-4 py-2.5" style={{ width: '30%' }}>Event</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Subtype</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">When</th>
            <th className="text-left px-4 py-2.5">Members</th>
            <th className="text-right px-4 py-2.5" style={{ width: 180 }} />
          </tr>
        </thead>
        <tbody>
          {grouped.map(([mk, groupCampaigns]) => (
            <React.Fragment key={mk}>
              <tr id={`month-${mk}`} className="sticky top-[37px] z-[5]">
                <td colSpan={6} className="px-4 pt-3 pb-2 bg-white dark:bg-nightshade-900">
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 whitespace-nowrap">
                      {monthLabel(mk)}
                    </span>
                    <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
                    <span className="text-[11px] font-mono text-nightshade-400 dark:text-nightshade-500">
                      {groupCampaigns.length}
                    </span>
                  </div>
                </td>
              </tr>
              {groupCampaigns.map(c => {
                const sc = subtypeColor(c.subtype);
                const pct = c.member_count > 0 ? Math.min(100, Math.round((c.member_count / 100) * 100)) : 0;
                return (
                  <tr key={c.id} className="group border-b border-nightshade-100 dark:border-nightshade-800 last:border-0 hover:bg-nightshade-50 dark:hover:bg-nightshade-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 font-medium">
                        <span className={`w-4 h-4 rounded flex-shrink-0 ${sc.dot}`} />
                        <button
                          onClick={() => onNavigate(c.id, 'manager')}
                          className="text-nightshade-900 dark:text-nightshade-100 hover:underline underline-offset-2 decoration-nightshade-300 dark:decoration-nightshade-600 text-left truncate"
                        >
                          {c.name}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><SubtypeChip subtype={c.subtype} /></td>
                    <td className="px-4 py-3"><LauncherStatusIcon status={c.launcher_status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-nightshade-700 dark:text-nightshade-300">{formatDate(c.event_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-nightshade-200 dark:bg-nightshade-700 overflow-hidden">
                          <div className="h-full rounded-full bg-tangerine-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-xs text-nightshade-700 dark:text-nightshade-300">{c.member_count.toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => onOpenDetails(c.id)}
                          title="Campaign details"
                          className="h-7 px-2 rounded-full bg-white dark:bg-nightshade-800 border-2 border-nightshade-200 dark:border-nightshade-600 text-nightshade-400 hover:text-blueberry-500 hover:border-blueberry-300 dark:hover:border-blueberry-600 transition-colors flex items-center"
                        >
                          <FileText className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onNavigate(c.id, 'manager')}
                          className="h-7 px-3 rounded-full bg-white dark:bg-nightshade-800 border-2 border-nightshade-300 dark:border-nightshade-500 text-[11px] font-medium text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-900 hover:text-white hover:border-nightshade-900 dark:hover:bg-nightshade-200 dark:hover:text-nightshade-900 transition-colors flex items-center gap-1"
                        >
                          <ClipboardList className="w-3 h-3" /> Manager
                        </button>
                        <button
                          onClick={() => onNavigate(c.id, 'launcher')}
                          className="h-7 px-3 rounded-full bg-tangerine-500 border-2 border-tangerine-500 text-[11px] font-medium text-white hover:bg-tangerine-600 hover:border-tangerine-600 transition-colors flex items-center gap-1"
                        >
                          <Rocket className="w-3 h-3" /> Dispatch
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function LauncherListPage({ eventManagerEnabled = false }: { eventManagerEnabled?: boolean } = {}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [view, setView] = useState<'calendar' | 'table'>(loadPersistedView);

  const today = todayIso();
  const [todayY, todayM] = today.split('-').map(Number);
  const [viewYear, setViewYear] = useState(todayY);
  const [viewMonth, setViewMonth] = useState(todayM - 1);

  const tableRef = useRef<HTMLDivElement | null>(null);

  const { campaigns, isLoading, isError } = useLauncherCampaigns();
  const { configs: _configs } = useCampaignTypeConfigs();
  const { syncStatus } = useSyncStatus();
  const triggerSync = useTriggerCampaignSync();

  // Unique subtypes from data
  const subtypes = useMemo(() => {
    const set = new Set<string>();
    for (const c of campaigns) { if (c.subtype) set.add(c.subtype); }
    return Array.from(set).sort();
  }, [campaigns]);

  const handleViewChange = (v: 'calendar' | 'table') => {
    setView(v);
    persistView(v);
  };

  const prevMonth = useCallback(() => {
    setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
  }, []);
  const nextMonth = useCallback(() => {
    setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
  }, []);
  const scrollToMonth = useCallback((year: number, month: number, smooth = true) => {
    requestAnimationFrame(() => {
      const mk = `${year}-${String(month).padStart(2, '0')}`;
      const el = document.getElementById(`month-${mk}`);
      if (el && tableRef.current) {
        // Scroll within the table container
        const container = tableRef.current;
        const elTop = el.offsetTop - container.offsetTop;
        container.scrollTo({ top: elTop, behavior: smooth ? 'smooth' : 'auto' });
      }
    });
  }, []);

  const goToday = useCallback(() => {
    const t = todayIso();
    const [ty, tm] = t.split('-').map(Number);
    setViewYear(ty);
    setViewMonth(tm - 1);
    scrollToMonth(ty, tm);
  }, [scrollToMonth]);

  const goToMonth = useCallback((year: number, month0: number) => {
    setViewYear(year);
    setViewMonth(month0);
    scrollToMonth(year, month0 + 1);
  }, [scrollToMonth]);

  // Auto-scroll table to current month on initial load
  const hasScrolled = useRef(false);
  useEffect(() => {
    if (!isLoading && campaigns.length > 0 && view === 'table' && !hasScrolled.current) {
      hasScrolled.current = true;
      scrollToMonth(todayY, todayM, false);
    }
  }, [isLoading, campaigns.length, view, scrollToMonth, todayY, todayM]);

  // Filter campaigns by subtype + search, sort by event_date ascending
  const filtered = useMemo(() => {
    return campaigns
      .filter(c => typeFilter === 'All' || c.subtype === typeFilter)
      .filter(c => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.type.toLowerCase().includes(q) || (c.subtype ?? '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.event_date ?? '').localeCompare(b.event_date ?? ''));
  }, [campaigns, typeFilter, search]);

  // Campaign details sidebar
  const [detailCampaignId, setDetailCampaignId] = useState<string | null>(null);
  const { campaign: detailCampaign } = useLauncherCampaign(detailCampaignId ?? '');
  const { apiFetch } = useApi();
  const { isMarketingUser, isAdmin } = useCurrentUser();
  const patchCampaignMutation = useMutation({
    mutationFn: (fields: Parameters<typeof patchCampaignDetails>[2]) =>
      patchCampaignDetails(apiFetch, detailCampaignId!, fields),
  });

  const handleNavigate = (id: string, target: 'manager' | 'launcher') => {
    if (target === 'manager' && eventManagerEnabled) {
      navigate(`/launcher/${id}/manager`);
    } else {
      navigate(`/launcher/${id}`);
    }
  };

  return (
    <main className="flex-1 overflow-auto bg-nightshade-50 dark:bg-nightshade-950">
      <div className="px-8 py-8">

        {/* Header */}
        <div className="mb-2">
          <h1 className="text-5xl font-bold tracking-tight leading-none text-nightshade-950 dark:text-nightshade-50">
            <span className="text-tangerine-600 dark:text-tangerine-400">Marketing</span> Toolkit
          </h1>
        </div>

        {/* Filter bar */}
        <div className="flex items-center mt-6 border border-nightshade-200 dark:border-nightshade-700 rounded-full px-2 py-1.5 h-14 bg-white dark:bg-nightshade-900">
          <div className="flex items-center gap-2 px-3 border-r border-nightshade-200 dark:border-nightshade-700 h-full overflow-x-auto">
            <button
              onClick={() => setTypeFilter('All')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${
                typeFilter === 'All'
                  ? 'bg-nightshade-900 dark:bg-nightshade-100 text-white dark:text-nightshade-900'
                  : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-100 dark:hover:bg-nightshade-800'
              }`}
            >
              All
            </button>
            {subtypes.map(st => {
              const c = subtypeColor(st);
              const isActive = typeFilter === st;
              return (
                <button
                  key={st}
                  onClick={() => setTypeFilter(st)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${
                    isActive
                      ? 'bg-nightshade-900 dark:bg-nightshade-100 text-white dark:text-nightshade-900'
                      : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-100 dark:hover:bg-nightshade-800'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${c.dot}`} />
                  {st}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 px-3 h-full ml-auto flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nightshade-400" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-40 pl-8 pr-3 py-1.5 text-xs bg-nightshade-100 dark:bg-nightshade-800 border-0 rounded-full text-nightshade-900 dark:text-nightshade-100 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 transition"
              />
            </div>
            <button
              onClick={() => triggerSync.mutate()}
              disabled={triggerSync.isPending}
              title={syncStatus?.campaign_hourly?.completed_at
                ? `Last synced ${formatRelativeTime(syncStatus.campaign_hourly.completed_at)}`
                : 'Sync campaigns from Salesforce'}
              className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-md hover:bg-nightshade-100 dark:hover:bg-nightshade-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${triggerSync.isPending ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/launcher/templates')}
              title="Template Admin"
              className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-md hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
            >
              <Wrench className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/launcher/settings')}
              title="Settings"
              className="p-1.5 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-md hover:bg-nightshade-100 dark:hover:bg-nightshade-700"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
            <ThemeToggle />
          </div>
        </div>

        {/* View tabs + nav */}
        <div className="flex items-center gap-1 mt-6">
          <button
            onClick={() => handleViewChange('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'calendar'
                ? 'bg-nightshade-900 dark:bg-nightshade-100 text-white dark:text-nightshade-900'
                : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 hover:text-nightshade-700 dark:hover:text-nightshade-200'
            }`}
          >
            <CalendarDays className="w-4 h-4" /> Calendar
          </button>
          <button
            onClick={() => handleViewChange('table')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'table'
                ? 'bg-nightshade-900 dark:bg-nightshade-100 text-white dark:text-nightshade-900'
                : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 hover:text-nightshade-700 dark:hover:text-nightshade-200'
            }`}
          >
            <LayoutList className="w-4 h-4" /> Table
          </button>

          <div className="flex-1" />

          {/* Calendar navigation (always visible for month context) */}
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} className="p-1.5 rounded-full border border-nightshade-200 dark:border-nightshade-700 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors">
              <ChevronLeft className="w-4 h-4 text-nightshade-600 dark:text-nightshade-300" />
            </button>
            <button onClick={goToday} className="px-3 py-1.5 rounded-full border border-nightshade-200 dark:border-nightshade-700 text-xs font-medium text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors">
              Today
            </button>
            <MonthPickerPopover viewYear={viewYear} viewMonth={viewMonth} onSelect={goToMonth} />
            <button onClick={nextMonth} className="p-1.5 rounded-full border border-nightshade-200 dark:border-nightshade-700 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors">
              <ChevronRight className="w-4 h-4 text-nightshade-600 dark:text-nightshade-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="mt-4">
          {isLoading ? (
            <div className="bg-white dark:bg-nightshade-900 rounded-2xl border border-nightshade-200 dark:border-nightshade-700 p-16 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-blueberry-400 border-t-transparent animate-spin" />
            </div>
          ) : isError ? (
            <div className="text-center py-16 text-sm text-pomegranate-500 dark:text-pomegranate-400">
              Failed to load campaigns. Please try again.
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-nightshade-400 dark:text-nightshade-500">
              No campaigns match your filters.
            </div>
          ) : view === 'calendar' ? (
            <MonthCalendar
              campaigns={filtered}
              viewYear={viewYear}
              viewMonth={viewMonth}
              onNavigate={handleNavigate}
            />
          ) : (
            <CampaignTable campaigns={filtered} onNavigate={handleNavigate} onOpenDetails={setDetailCampaignId} tableRef={tableRef} />
          )}
        </div>
      </div>

      {/* Campaign detail sidebar (overlay) */}
      {detailCampaignId && detailCampaign && (
        <CampaignDetailSidebar
          campaign={detailCampaign}
          onClose={() => setDetailCampaignId(null)}
          onSave={async (fields) => {
            await patchCampaignMutation.mutateAsync(fields);
            setDetailCampaignId(null);
          }}
          saving={patchCampaignMutation.isPending}
          readOnly={!(isMarketingUser || isAdmin)}
        />
      )}
    </main>
  );
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
